import type { ReactNode } from 'react';

/**
 * A named group of fields inside a form — "Present address", "Permanent address".
 *
 * **It carries vertical margin, and that is the whole reason it exists as a component.** A
 * modal body already spaces its children with `gap-4`, which is the gap BETWEEN FIELDS. A
 * group of fields under a heading is not another field: it is a section, and set at the same
 * rhythm as the inputs around it the legend reads as a label belonging to the control
 * directly beneath rather than as a heading over the several that follow.
 *
 * `my-3` on top of the parent's `gap-4` puts 28px either side — enough that the boundary is
 * visible without a rule, which a form this short does not need.
 *
 * **It is a real `<fieldset>` with a real `<legend>`**, so a screen reader announces the
 * group's name with every field inside it. A `<p>` heading over a `<div>` looks the same and
 * says nothing.
 *
 * Not to be confused with two neighbours:
 *  - [`<FieldGroup>`](./FieldGroup.tsx) joins segments that make up ONE value — a surname and
 *    its suffix — into a single bordered control. This groups SEVERAL values under a name.
 *  - [`<RadioGroup>`](./RadioGroup.tsx) is itself a `<fieldset>`, because a set of radios is
 *    one control. It takes no margin of its own: it sits in the run of fields, not around it.
 */
export function FormFieldset({
  legend,
  children,
  className = '',
}: {
  legend: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={`my-3 flex flex-col gap-4 ${className}`}>
      {/*
        Darker than the field labels under it, and with a gap of its own.

        `slate-800` rather than the `slate-700` a field label takes: the legend names the
        SECTION, and at the same tone as the labels below it read as one more label belonging
        to the first field. That is the `<ProfileCard>` title's tone, which is the same job —
        naming a group of facts. Not `slate-900`, which the product reserves for whatever is
        currently selected.

        `mb-2` because a `<legend>` is not a flex item: the fieldset's `gap-4` spaces the
        FIELDS and never reaches it, so without a margin the legend sits flush against the
        first label and the two read as a two-line heading.
      */}
      <legend className="mb-2 text-sm text-slate-800">{legend}</legend>
      {children}
    </fieldset>
  );
}
